import { Popover } from '@/components/utils/Popover/Popover';
import { CANVAS_SIZE, IMAGE_COMPRESS_MAX_SIZE, IMAGE_COMPRESS_QUALITY } from '@/constant';
import { capture } from '@/handlers/error';
import { Size } from '@/types/slide';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Button, Chip, CircularProgress, InputAdornment, MenuItem, Stack, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useNotifications } from '@toolpad/core';
import React, { useState } from 'react';
import Dropzone from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import Resizer from 'react-image-file-resizer';

const SIZE = {
    width: 440,
    height: 640,
};

export type ImagePickerLibrarySourceType = {
    path: string;
    value: string;
    label: string;
    size: Size;
};

type ImagePickerProps = {
    value: string;
    imageURL: string;
    size?: Size;
};

type SourceType = 'library' | 'upload';

interface Props {
    title: string;
    description?: string;
    librarySources: ImagePickerLibrarySourceType[];
    anchorEl: HTMLElement | null;
    value: ImagePickerProps;
    onClose: () => void;
    onUpdate: (settings: ImagePickerProps) => void;
    onUpload?: (file: File) => Promise<string>;
    slotProps?: { paper?: { ref: React.Ref<HTMLDivElement> } };
    disableSearch?: boolean;
    type: 'image' | 'background';
}

export const ImagePicker: React.FC<Props> = ({ title, anchorEl, onClose, onUpdate, onUpload, value, librarySources, disableSearch, type, slotProps, description }) => {
    const { t } = useTranslation();

    const notifications = useNotifications();
    const open = Boolean(anchorEl);

    const [searchResult, setSearchResult] = useState('');
    const [source, setSource] = useState<SourceType>(value.imageURL ? 'upload' : 'library');

    const [uploading, setUploading] = useState(false);

    const resizeFile = React.useCallback(
        (file: File) =>
            new Promise((resolve) => {
                const [, fileType] = file.type.split('/');
                if (!fileType) {
                    return null;
                }
                const fileTypeUpper = fileType.toUpperCase();

                const outputFormat = fileTypeUpper === 'JPEG' ? 'JPEG' : 'PNG';
                // Set output format based on input file type

                Resizer.imageFileResizer(
                    file,
                    IMAGE_COMPRESS_MAX_SIZE.width,
                    IMAGE_COMPRESS_MAX_SIZE.height,
                    outputFormat,
                    IMAGE_COMPRESS_QUALITY,
                    0,
                    (uri) => {
                        resolve(uri);
                    },
                    'file',
                );
            }),
        [],
    );

    const getImageSize = React.useCallback(async (file: File) => {
        const img = new Image();
        img.src = URL.createObjectURL(file);
        const size = await new Promise<Size>((resolve) => {
            img.onload = () => {
                const width = img.width;
                const height = img.height;
                URL.revokeObjectURL(img.src);
                resolve({ width, height });
            };
        });

        return size;
    }, []);

    const onUploadChange = React.useCallback(
        async (imageList: File[]) => {
            try {
                setUploading(true);

                if (imageList && imageList.length > 0) {
                    const lastImage = imageList[0];
                    if (!lastImage) {
                        throw new Error('No valid image file selected');
                    }

                    const newFile = await resizeFile(lastImage);
                    if (!newFile) {
                        throw new Error('Failed to process image');
                    }

                    if (!newFile || !(newFile instanceof File)) {
                        throw new Error('Invalid file format');
                    }

                    const size = await getImageSize(newFile);

                    const url = await onUpload?.(newFile);
                    if (url) {
                        onUpdate({ ...value, value: '', imageURL: url, size: size });
                    }
                }
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                capture(error);
            } finally {
                setUploading(false);
            }
        },
        [resizeFile, getImageSize, onUpload, onUpdate, value, notifications],
    );

    const searchRes = librarySources.filter((item) => item.label.toLowerCase().includes(searchResult.toLowerCase()));

    return (
        <Popover
            data-testid='image-picker'
            title={title}
            open={open}
            anchorEl={anchorEl}
            onClose={() => {
                onClose();
            }}
            anchorOrigin={{
                vertical: 'center',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'center',
                horizontal: -8,
            }}
            slotProps={{
                paper: {
                    ref: slotProps?.paper?.ref,
                    sx: {
                        width: SIZE.width,
                        height: SIZE.height,
                        display: 'flex',
                        p: 2,
                    },
                },
            }}
        >
            {description && (
                <Typography variant='body1' color='text.secondary' sx={{ mb: 3 }}>
                    {description}
                </Typography>
            )}

            <Stack spacing={1}>
                <TextField
                    label={t('slide.components.assets.image.source')}
                    select
                    fullWidth
                    value={source}
                    onChange={(e) => {
                        setSource(e.target.value as SourceType);
                    }}
                >
                    {[
                        { value: 'library', label: t('slide.components.assets.image.library') },
                        { value: 'upload', label: t('slide.components.assets.image.upload') },
                    ].map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </TextField>

                {!disableSearch && source === 'library' && (
                    <TextField
                        label={t('slide.components.assets.image.search')}
                        fullWidth
                        onChange={(e) => {
                            setSearchResult(e.target.value);
                        }}
                        defaultValue={searchResult}
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position='end'>
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                )}
            </Stack>

            {source === 'library' && (
                <Box
                    sx={(theme) => ({
                        mt: 2,
                        flex: 1,
                        display: 'flex',
                        alignContent: 'center',
                        overflow: 'auto',
                        backgroundColor: (theme.vars || theme).palette.grey[200],
                        ...theme.applyStyles('dark', {
                            backgroundColor: (theme.vars || theme).palette.grey[700],
                        }),
                    })}
                >
                    <Box
                        sx={{
                            flex: 1,
                            overflowY: 'auto',
                            display: 'flex',
                            px: 1,
                            my: 1.5,
                        }}
                    >
                        {searchRes.length <= 0 ? (
                            <Box sx={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                                <Typography>{t('slide.components.assets.image.noResultFor', { keyword: searchResult })}</Typography>
                            </Box>
                        ) : (
                            <Box sx={{ flex: 1 }}>
                                <Grid container spacing={1} sx={{ justifyContent: 'space-evenly' }}>
                                    {searchRes.map((item) => (
                                        <Grid
                                            key={item.path}
                                            sx={{
                                                position: 'relative',
                                                width: 120,
                                                height: 120,
                                                backgroundColor: (theme) => (theme.vars || theme).palette.background.default,
                                                borderRadius: 1,
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                border: '2px solid',
                                                borderColor: 'transparent',
                                                '&:hover': {
                                                    opacity: 0.8,
                                                    border: '2px solid',
                                                    borderColor: 'primary.main',
                                                },
                                                p: 1,
                                            }}
                                            onClick={() => {
                                                onUpdate({ ...value, value: item.value, size: item.size, imageURL: '' });
                                                onClose();
                                            }}
                                        >
                                            <img src={`${item.path}`} alt={item.label} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }} />
                                            <Chip label={item.label} sx={{ position: 'absolute', right: 4, top: 4 }} />
                                        </Grid>
                                    ))}
                                    {searchRes.length % 3 !== 0 &&
                                        // Fill the remaining space with empty boxes just to make it looks nice with space-evenly
                                        Array.from({ length: 3 - (searchRes.length % 3) }).map((_, index) => (
                                            <Grid
                                                key={`empty-${index}`}
                                                sx={{
                                                    width: 120,
                                                    height: 120,
                                                    backgroundColor: 'transparent',
                                                    borderRadius: 1,
                                                }}
                                            />
                                        ))}
                                </Grid>
                            </Box>
                        )}
                    </Box>
                </Box>
            )}

            {source === 'upload' && (
                <Box sx={{ display: 'flex', flex: 1, mt: 2 }}>
                    {uploading ? (
                        <Box sx={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center' }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center' }}>
                            {value.imageURL ? (
                                <Stack alignItems='center' spacing={2}>
                                    <Box
                                        component='img'
                                        src={value.imageURL}
                                        sx={{
                                            maxWidth: 380,
                                            maxHeight: 380,
                                            ...(type === 'background' ? { aspectRatio: CANVAS_SIZE.width / CANVAS_SIZE.height, objectFit: 'cover' } : {}),
                                        }}
                                    />

                                    <Button
                                        variant='outlined'
                                        startIcon={<FileUploadIcon />}
                                        sx={{ width: 'fit-content', alignSelf: 'center' }}
                                        onClick={() => onUpdate({ ...value, value: '', imageURL: '' })}
                                    >
                                        {t('slide.components.assets.image.uploadAgain')}
                                    </Button>
                                </Stack>
                            ) : (
                                <Dropzone
                                    onDropAccepted={onUploadChange}
                                    onDropRejected={() => notifications.show(t('slide.components.assets.image.uploadRejected'), { severity: 'warning', autoHideDuration: 5000 })}
                                    accept={{
                                        'image/png': ['.png'],
                                        'image/jpeg': ['.jpg'],
                                    }}
                                    maxFiles={1}
                                    multiple={false}
                                >
                                    {({ getRootProps, getInputProps }) => (
                                        <Box
                                            sx={(theme) => ({
                                                p: 1,
                                                flex: 1,
                                                border: 1,
                                                borderRadius: 0.5,
                                                borderStyle: 'dashed',
                                                borderColor: (theme.vars || theme).palette.grey[300],
                                                height: '100%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor: (theme.vars || theme).palette.grey[100],
                                                ...theme.applyStyles('dark', {
                                                    backgroundColor: (theme.vars || theme).palette.grey[700],
                                                }),
                                            })}
                                            {...getRootProps({ className: 'dropzone' })} // Pass the onDrop handler to the getRootProps function
                                        >
                                            <input {...getInputProps()} />
                                            <Stack direction='column' spacing={1.5} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                <Typography>{t('slide.components.assets.image.dropYourFileHere')}</Typography>
                                                <Typography>{t('slide.components.assets.image.or')}</Typography>
                                                <Button variant='outlined' startIcon={<FileUploadIcon />}>
                                                    <Typography sx={{ textAlign: 'center' }}>{t('slide.components.assets.image.uploadHere')}</Typography>
                                                </Button>
                                            </Stack>
                                        </Box>
                                    )}
                                </Dropzone>
                            )}
                        </Box>
                    )}
                </Box>
            )}
        </Popover>
    );
};
